import { Helmet } from '@dr.pogodin/react-helmet';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import Loading from '../common/lib/Loading';
import PageNotFound from '../common/lib/PageNotFound';
import useJson from '../common/lib/useJson';
import XoopsCode from '../common/lib/XoopsCode';
import type { MultiLang } from '../config';
import Functions from '../functions';
import NimgdocsBulletinLog from './NimgdocsBulletinLog';
import NimgdocsTutorials from './NimgdocsTutorials';

interface Props {
    lang: MultiLang;
}

type NimgdocsDocument = Record<MultiLang, string>;

// `docname` of the original module and the file it was dumped to under modules/nimgdocs/docs/.
const DOCUMENTS: Record<string, string> = {
    about_NIMGPF: 'about',
    additem: 'additem',
    introduction: 'introduction',
    nimgcenter: 'nimgcenter',
    nimgsearch: 'nimgsearch',
    '3DBrowse': '3dbrowse',
    '3DBrowse_usage': '3dbrowse_usage',
};

const NimgdocsDocumentPage = (props: Props & { file: string }) => {
    const { lang, file } = props;
    const doc = useJson<NimgdocsDocument>(`/modules/nimgdocs/docs/${file}.json`);
    if (typeof doc === 'undefined') {
        return <Loading />;
    }
    if (doc === null) {
        return <PageNotFound lang={lang} />;
    }
    return (
        <>
            <Helmet>
                <title>NIMG Documents - {Functions.siteTitle(lang)}</title>
            </Helmet>
            <XoopsCode lang={lang} text={doc[lang]} dohtml={true} />
        </>
    );
};

const NimgdocsPage = (props: Props) => {
    const { lang } = props;
    const { search } = useLocation();
    const docname = new URLSearchParams(search).get('docname') ?? '';
    if (!Object.hasOwn(DOCUMENTS, docname)) {
        return <PageNotFound lang={lang} />;
    }
    return <NimgdocsDocumentPage lang={lang} file={DOCUMENTS[docname]} />;
};

const Nimgdocs = (props: Props) => {
    const { lang } = props;
    return (
        <Routes>
            <Route index element={<Navigate to="/modules/nimgdocs/index.php?docname=about_NIMGPF" replace />} />
            <Route path="index.php" element={<NimgdocsPage lang={lang} />} />
            <Route path="tutorials/index.php" element={<Navigate to="/modules/nimgdocs/tutorials/" replace />} />
            <Route path="tutorials/" element={<NimgdocsTutorials lang={lang} />} />
            <Route path="bulletin/log.php" element={<NimgdocsBulletinLog lang={lang} />} />
            <Route path="*" element={<PageNotFound lang={lang} />} />
        </Routes>
    );
};

export default Nimgdocs;
